import { getIntl } from 'umi';

import ProcessInstanceNumberingPeriodKind from '@/utils/domain/constants/enumerator/ProcessInstanceNumberingPeriodKind';

const ACN_TOKEN_RE = /\{ACN[IYMD]#\d+\}/;
const ACN_DAILY_RE = /\{ACND#\d+\}/;
const ACN_MONTHLY_RE = /\{ACNM#\d+\}/;
const ACN_YEARLY_RE = /\{ACNY#\d+\}/;
const ACN_CONTINUOUS_RE = /\{ACNI#\d+\}/;
const YEAR_TOKEN_RE = /\{CY\}|\{CY2D\}/;
const MONTH_TOKEN_RE = /\{CMN\}|\{CMT\}/;
const DAY_TOKEN_RE = /\{CD\}/;

export const INSTANCES_NAME_PATTERN_MAX_LENGTH = 150;

export const NAME_PATTERNS_STEP_ID = 'step2';

/** Fields that must never ride on Process create/edit payloads after the cutover. */
export const PROCESS_NUMBERING_LEGACY_FIELDS = [
  'instancesNamePattern',
  'lastGeneratedNumber',
  'periodKind',
  'currentYear',
  'currentMonth',
  'currentDay',
  'lastAllocatedAt',
  'automaticNumbersCurrentYear',
  'automaticNumbersCurrentMonth',
  'automaticNumbersCurrentDay',
];

/**
 * Derives period kind from pattern tokens.
 * Precedence: Daily > Monthly > Yearly > Continuous.
 */
export function derivePeriodKind(pattern) {
  if (!pattern || typeof pattern !== 'string') return null;
  if (ACN_DAILY_RE.test(pattern)) return ProcessInstanceNumberingPeriodKind.Daily;
  if (ACN_MONTHLY_RE.test(pattern)) return ProcessInstanceNumberingPeriodKind.Monthly;
  if (ACN_YEARLY_RE.test(pattern)) return ProcessInstanceNumberingPeriodKind.Yearly;
  if (ACN_CONTINUOUS_RE.test(pattern)) return ProcessInstanceNumberingPeriodKind.Continuous;
  return null;
}

export function hasAutomaticNumberToken(pattern) {
  return ACN_TOKEN_RE.test(pattern || '');
}

/**
 * Yearly/monthly/daily ACN tokens require matching date identity tokens in the pattern.
 */
export function hasValidPeriodIdentity(pattern) {
  const kind = derivePeriodKind(pattern);
  if (kind == null) return false;
  if (kind === ProcessInstanceNumberingPeriodKind.Continuous) return true;
  if (kind === ProcessInstanceNumberingPeriodKind.Yearly) {
    return YEAR_TOKEN_RE.test(pattern);
  }
  if (kind === ProcessInstanceNumberingPeriodKind.Monthly) {
    return YEAR_TOKEN_RE.test(pattern) && MONTH_TOKEN_RE.test(pattern);
  }
  if (kind === ProcessInstanceNumberingPeriodKind.Daily) {
    return (
      YEAR_TOKEN_RE.test(pattern) && MONTH_TOKEN_RE.test(pattern) && DAY_TOKEN_RE.test(pattern)
    );
  }
  return false;
}

function formatRuleMessage(id) {
  return getIntl().formatMessage({ id });
}

/** Ant Design rules for instancesNamePattern (create + numbering screen). */
export function instancesNamePatternRules() {
  return [
    { required: true, whitespace: true },
    { max: INSTANCES_NAME_PATTERN_MAX_LENGTH },
    {
      validator: (_, value) => {
        if (value === undefined || value === null || value === '') {
          return Promise.resolve();
        }
        if (!hasAutomaticNumberToken(value)) {
          return Promise.reject(
            new Error(formatRuleMessage('processes.form.instancesNamePattern.acnRequired')),
          );
        }
        if (!hasValidPeriodIdentity(value)) {
          return Promise.reject(
            new Error(formatRuleMessage('processes.form.instancesNamePattern.periodIdentity')),
          );
        }
        return Promise.resolve();
      },
    },
  ];
}

/**
 * Prefer counters from the PUT response. The server may zero lastGeneratedNumber
 * when the period kind changes. Keep the submitted pattern only when the
 * response does not include one, so the process edit preview stays in sync.
 */
export function mergeNumberingSaveResult(response, submitted = {}) {
  const hasEntity = !!response && typeof response === 'object' && !Array.isArray(response);
  const server = hasEntity ? response : {};
  const serverPattern = server.instancesNamePattern;
  const hasServerPattern = typeof serverPattern === 'string' && serverPattern !== '';

  const merged = {
    ...server,
    instancesNamePattern: hasServerPattern ? serverPattern : submitted.instancesNamePattern,
  };

  if (!hasEntity || server.lastGeneratedNumber == null || server.lastGeneratedNumber === '') {
    merged.lastGeneratedNumber = Number(submitted.lastGeneratedNumber) || 0;
  }

  return merged;
}

/** Nest create-only pattern under processInstanceNumbering; drop legacy flat fields. */
export function buildProcessCreatePayload(formData) {
  const payload = { ...formData };
  const instancesNamePattern = payload.instancesNamePattern;

  PROCESS_NUMBERING_LEGACY_FIELDS.forEach((key) => {
    delete payload[key];
  });

  payload.processInstanceNumbering = {
    instancesNamePattern,
  };

  return payload;
}

/** Ensure edit/detail PUT never implies numbering ownership. */
export function stripProcessNumberingFields(formData) {
  const payload = { ...formData };
  PROCESS_NUMBERING_LEGACY_FIELDS.forEach((key) => {
    delete payload[key];
  });
  delete payload.processInstanceNumbering;
  return payload;
}

/**
 * Create payload nests flat wizard fields under these API object names.
 * Used only when the API reports the object itself, without a child segment.
 */
const PROCESS_CREATE_FIELD_PREFIXES = {
  processInstanceNumbering: ['instancesNamePattern'],
};

const NUMBERING_FORM_FIELD_NAMES = [
  'instancesNamePattern',
  'lastGeneratedNumber',
  'periodKind',
  'currentYear',
  'currentMonth',
  'currentDay',
  'lastAllocatedAt',
];

/** .NET / PascalCase segment → form camelCase (`InstancesNamePattern` → `instancesNamePattern`). */
function normalizeFieldSegment(segment) {
  const withoutIndex = String(segment).replace(/\[\d+\]/g, '');
  if (!withoutIndex) return '';
  return withoutIndex.charAt(0).toLowerCase() + withoutIndex.slice(1);
}

/**
 * Resolve `validationErrors[].field` against the wizard/form field names.
 * Supports flat names, dotted paths and PascalCase segments.
 * Returns the form `name`, or null when the path does not belong to this form.
 */
function resolveProcessFormField(apiField, fieldNames) {
  if (typeof apiField !== 'string' || !apiField.trim()) return null;

  const names = [...new Set(fieldNames.filter((name) => typeof name === 'string' && name))];
  const byLower = new Map(names.map((name) => [name.toLowerCase(), name]));

  const match = (candidate) => {
    if (!candidate) return null;
    if (names.includes(candidate)) return candidate;
    return byLower.get(String(candidate).toLowerCase()) || null;
  };

  const direct = match(apiField.trim());
  if (direct) return direct;

  const segments = apiField
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);
  const camelSegments = segments.map(normalizeFieldSegment).filter(Boolean);

  const suffixes = [];
  for (let i = 0; i < camelSegments.length; i += 1) {
    suffixes.push(camelSegments.slice(i).join('.'));
  }
  for (let i = 0; i < segments.length; i += 1) {
    suffixes.push(segments.slice(i).join('.'));
  }

  const withoutIndexes = apiField.replace(/\[.*?\]/g, '');
  if (withoutIndexes !== apiField) {
    suffixes.push(withoutIndexes);
    suffixes.push(normalizeFieldSegment(withoutIndexes));
  }

  for (let i = 0; i < suffixes.length; i += 1) {
    const hit = match(suffixes[i]);
    if (hit) return hit;
  }

  const nestedChildren = PROCESS_CREATE_FIELD_PREFIXES[camelSegments[0]];
  if (nestedChildren && segments.length === 1) {
    const available = nestedChildren.filter((name) => names.includes(name));
    if (available.length === 1) return available[0];
  }

  return null;
}

/**
 * Split API validation errors into ones that belong to a wizard field
 * and ones that must keep the generic notification.
 */
export function partitionProcessValidationErrors(validationErrors, groups = []) {
  const catalog = [];
  groups.forEach((group, stepIndex) => {
    (group.fields || []).forEach((field) => {
      if (typeof field?.name === 'string' && field.name) {
        catalog.push({ name: field.name, stepIndex });
      }
    });
  });

  const fieldNames = catalog.map((entry) => entry.name);
  const mapped = [];
  const unmapped = [];

  (Array.isArray(validationErrors) ? validationErrors : []).forEach((item) => {
    const resolved = resolveProcessFormField(item?.field, fieldNames);
    if (!resolved) {
      unmapped.push(item);
      return;
    }
    const stepIndex = catalog.find((entry) => entry.name === resolved)?.stepIndex ?? 0;
    mapped.push({ ...item, field: resolved, stepIndex });
  });

  return { mapped, unmapped };
}

/**
 * Map API nested validation paths (e.g. processInstanceNumbering.InstancesNamePattern)
 * onto flat form field names so FormBuilder can highlight them.
 */
export function normalizeNumberingValidationErrors(validationErrors, fields) {
  if (!Array.isArray(validationErrors)) return validationErrors;

  const fieldNames =
    Array.isArray(fields) && fields.length
      ? fields
          .map((field) => (typeof field === 'string' ? field : field?.name))
          .filter((name) => typeof name === 'string' && name)
      : NUMBERING_FORM_FIELD_NAMES;

  return validationErrors.map((item) => {
    const resolved = resolveProcessFormField(item?.field, fieldNames);
    if (!resolved) return item;
    return { ...item, field: resolved };
  });
}

function readMetadataText(metadata, ...keys) {
  if (!metadata || typeof metadata !== 'object') return '';
  for (let i = 0; i < keys.length; i += 1) {
    const value = metadata[keys[i]];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

/**
 * Conflict identity attached by the API (`validationErrors[].metadata`).
 * Accepts camelCase and PascalCase. Returns null when nothing identifies a process.
 */
export function readPatternConflictMetadata(metadata) {
  const name = readMetadataText(metadata, 'name', 'Name');
  const instancesNamePattern = readMetadataText(
    metadata,
    'instancesNamePattern',
    'InstancesNamePattern',
  );
  const id = readMetadataText(metadata, 'id', 'Id');
  if (!name && !instancesNamePattern && !id) return null;
  return { id, name, instancesNamePattern };
}

function mentionsValue(message, value) {
  if (!value || value.length < 2) return false;
  return String(message).toLowerCase().includes(value.toLowerCase());
}

/**
 * The conflict sentence repeats the process name and pattern already shown in the
 * rows. Keep the closing sentence from the API text when that happens.
 */
function conflictExplanation(message, metadata) {
  const normalized = String(message || '').trim();
  if (!normalized) return '';
  const sentences = normalized.split(/(?<=[.!?])\s+/).filter(Boolean);
  if (sentences.length < 2) return normalized;
  const tail = sentences[sentences.length - 1].trim();
  const head = sentences.slice(0, -1).join(' ');
  const headMentionsConflict =
    mentionsValue(head, metadata?.name) || mentionsValue(head, metadata?.instancesNamePattern);
  if (!headMentionsConflict || !tail) return normalized;
  return tail;
}

/**
 * Turn one or more API errors for `instancesNamePattern` into the reference layout:
 * conflict card (metadata + explanation) and secondary notices.
 * Messages stay the API strings. Static labels are i18n in the component.
 */
export function presentInstancesNamePatternValidation(items) {
  const list = Array.isArray(items) ? items : [];
  const messages = [];
  let conflict = null;

  list.forEach((item) => {
    if (!conflict) conflict = readPatternConflictMetadata(item?.metadata);
    const texts = Array.isArray(item?.errorMessages) ? item.errorMessages : [];
    texts.forEach((text) => {
      if (typeof text === 'string' && text.trim()) messages.push(text.trim());
    });
  });

  if (!conflict) {
    return { conflict: null, explanations: messages, notices: [] };
  }

  const explanations = [];
  const notices = [];
  messages.forEach((text) => {
    const isConflictMessage =
      mentionsValue(text, conflict.name) || mentionsValue(text, conflict.instancesNamePattern);
    if (isConflictMessage) explanations.push(conflictExplanation(text, conflict));
    else notices.push(text);
  });

  if (!explanations.length && messages.length) {
    const last = messages[messages.length - 1];
    return {
      conflict,
      explanations: [conflictExplanation(last, conflict)],
      notices: messages.slice(0, -1),
    };
  }

  return { conflict, explanations, notices };
}
